import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { MoreVertical, Trash2, Edit, MessageSquare } from 'lucide-react';
import { Project } from '../../../types/project';
import { DropdownMenu, DropdownMenuItem } from '../../common';

interface ProjectCardProps {
  project: Project;
  onEdit: (project: Project) => void;
  onDelete: (id: string) => Promise<void>;
  formatRelativeTime: (date: string) => string;
}

/**
 * ProjectCard - Card de projeto individual
 *
 * Exibe:
 * - Nome do projeto
 * - Descrição
 * - Contagem de conversas e arquivos
 * - Tempo da última atualização
 * - Menu de contexto (editar/excluir)
 */
export const ProjectCard = ({
  project,
  onEdit,
  onDelete,
  formatRelativeTime
}: ProjectCardProps) => {
  const navigate = useNavigate();
  const [showMenu, setShowMenu] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);

  const handleClick = () => {
    navigate(`/project/${project.id}`);
  };

  const handleEdit = (e: React.MouseEvent) => {
    e.stopPropagation();
    setShowMenu(false);
    onEdit(project);
  };

  const handleDelete = async (e: React.MouseEvent) => {
    e.stopPropagation();
    setShowMenu(false);

    try {
      setIsDeleting(true);
      await onDelete(project.id);
    } catch (error) {
      console.error('Error deleting project:', error);
    } finally {
      setIsDeleting(false);
    }
  };

  const handleMenuClick = (e: React.MouseEvent) => {
    e.stopPropagation();
    setShowMenu(!showMenu);
  };

  const menuItems: DropdownMenuItem[] = [
    {
      label: 'Editar',
      icon: <Edit className="w-4 h-4" />,
      onClick: handleEdit,
    },
    {
      label: 'Excluir',
      icon: <Trash2 className="w-4 h-4" />,
      onClick: handleDelete,
      variant: 'danger',
    },
  ];

  return (
    <div
      onClick={handleClick}
      className={`
        relative bg-[#1a1a1a] rounded-xl p-5 cursor-pointer
        border-l-4 border-l-transparent
        border-t border-r border-b border-[#333333]
        hover:border-l-[#F4CC04]
        hover:bg-[#333333]
        transition-colors duration-200
        ${isDeleting ? 'opacity-50 pointer-events-none' : ''}
      `}
    >
      {/* Header with title and menu */}
      <div className="flex justify-between items-start mb-3">
        <h3 className="text-white font-semibold text-lg pr-8">
          {project.name}
        </h3>

        {/* Menu button */}
        <div className="relative">
          <button
            onClick={handleMenuClick}
            className="p-1 rounded-lg text-[#666666] hover:text-white hover:bg-[#333333] transition-colors"
          >
            <MoreVertical className="w-5 h-5" />
          </button>

          <DropdownMenu
            isOpen={showMenu}
            onClose={() => setShowMenu(false)}
            items={menuItems}
          />
        </div>
      </div>

      {/* Description */}
      <p className="text-[#666666] text-sm mb-4 line-clamp-2 min-h-[40px]">
        {project.description || 'Sem descrição'}
      </p>

      {/* Footer with stats */}
      <div className="flex items-center justify-between text-sm">
        <div className="flex items-center gap-4">
          {/* Conversations count */}
          <div className="flex items-center gap-1.5 text-[#666666]">
            <MessageSquare className="w-4 h-4" />
            <span>{project.chats_count || 0} conversas</span>
          </div>
        </div>

        {/* Last updated */}
        <span className="text-[#555555] text-xs">
          {formatRelativeTime(project.updated_at)}
        </span>
      </div>
    </div>
  );
};
